/*
 * The shell: a sidebar down the left, a status strip across the top.
 *
 * WHY A LEFT SIDEBAR AND NOT TABS ALONG THE TOP
 *
 * Every serious trading front end does this — Kite, TradingView, Groww,
 * Interactive Brokers — and for the same reason. The top strip on a trading
 * screen is the most valuable space on the page, because it is where the state
 * that must never be scrolled away lives: is the market open, is the feed
 * alive, what is today's P&L, and where is the button that stops everything.
 * Spending that row on navigation means either losing the status or pushing it
 * down where it scrolls out of sight — and a panic button you have to scroll
 * to find is not a panic button.
 *
 * Navigation is also a list that grows. A sidebar takes an eighth item without
 * reflowing; a tab bar starts wrapping or hiding things behind "more".
 *
 * It collapses to icons, and does so on its own below 820px. The choice is
 * remembered in localStorage, because it is a preference about this person's
 * screen and not something worth a row in a database.
 *
 * THE TOP STRIP IS THE SAFETY SURFACE. Four things are always visible, on
 * every page, without scrolling: whether the market is open, whether prices
 * are actually arriving, what the day has made or lost, and the halt button.
 * Everything else can be navigated to.
 */

import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { useLive } from "./api/stream";
import { useLogout, usePanic, useSetHalted, useStatus, useSummary } from "./api/hooks";
import type { User } from "./api/types";
import {
  IconBolt,
  IconChart,
  IconChevron,
  IconDashboard,
  IconHelp,
  IconLogout,
  IconOrders,
  IconPeople,
  IconPositions,
  IconSettings,
  IconSignals,
  IconStrategy,
} from "./components/Icons";
import { EnvironmentSwitch } from "./components/EnvironmentSwitch";
import { Modal } from "./components/Modal";
import { useToast } from "./components/Toast";
import { ago, signedRupees, toneOf } from "./lib/format";

const COLLAPSE_KEY = "invest.nav.collapsed";

export function Layout({ user, children }: { user: User; children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === "1",
  );
  const [panicOpen, setPanicOpen] = useState(false);

  const status = useStatus();
  const summary = useSummary(status.data?.marketOpen);
  const logout = useLogout();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const openPositions = status.data?.openPositions ?? 0;
  const halted = status.data?.halted ?? true;

  return (
    <div
      className={`shell ${collapsed ? "collapsed" : ""} ${
        status.data?.environment === "live" ? "env-live" : ""
      }`}
    >
      <div className="brand-cell">
        <div className="brand-mark" aria-hidden="true">
          <svg width="17" height="17" viewBox="0 0 32 32" fill="none">
            <path
              d="M5 22 L12 15 L17 19 L27 8"
              stroke="#2fd07a"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="27" cy="8" r="2.6" fill="#2fd07a" />
          </svg>
        </div>
        <div className="brand-name">
          HKM Invest
          <small>{status.data?.environment === "live" ? "Real money" : "Paper trading"}</small>
        </div>
      </div>

      <nav className="nav" aria-label="Sections">
        <NavItem to="/" icon={<IconDashboard />} label="Dashboard" end />
        <NavItem to="/charts" icon={<IconChart />} label="Charts" />

        <div className="nav-group-label">Trading</div>
        <NavItem
          to="/strategies"
          icon={<IconStrategy />}
          label="Strategies"
          tag={status.data?.enabledStrategies || undefined}
        />
        <NavItem
          to="/positions"
          icon={<IconPositions />}
          label="Positions"
          tag={openPositions || undefined}
        />
        <NavItem to="/orders" icon={<IconOrders />} label="Orders" />
        <NavItem to="/signals" icon={<IconSignals />} label="Signals" />

        <div className="nav-group-label">Account</div>
        <NavItem to="/settings" icon={<IconSettings />} label="Settings" />
        {user.isAdmin && <NavItem to="/people" icon={<IconPeople />} label="People" />}
        <NavItem to="/help" icon={<IconHelp />} label="How it works" />

        <div className="nav-spacer" />

        <button
          className="nav-item"
          style={{ background: "none", border: "1px solid transparent", cursor: "pointer", textAlign: "left" }}
          onClick={() => {
            logout.mutate(undefined, { onSuccess: () => navigate("/") });
          }}
        >
          <span className="icon"><IconLogout /></span>
          <span className="label">Sign out</span>
        </button>

        <button
          className="nav-collapse"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          <IconChevron
            size={15}
            className={collapsed ? "flip" : undefined}
          />
        </button>
      </nav>

      <header className="topbar">
        {/* First thing on the bar, before the market state — it is the frame
            everything else on the screen should be read inside. */}
        <EnvironmentSwitch />
        <MarketState open={status.data?.marketOpen} nowIst={status.data?.nowIst} />
        <FeedChip />
        {halted && status.data?.connected && (
          <span className="pill danger" title={status.data?.haltReason ?? undefined}>
            Halted
          </span>
        )}
        {status.data?.engineEnabled === false && (
          <span className="pill off" title="No strategy will run until the engine is switched on.">
            Engine off
          </span>
        )}

        <div className="topbar-spacer" />

        <div className="pnl-chip">
          <span className="k">Today</span>
          <span className={`v ${toneOf(summary.data?.realised)}`}>
            {signedRupees(summary.data?.realised ?? 0)}
          </span>
        </div>
        <div className="pnl-chip">
          <span className="k">Trades</span>
          <span className="v">{summary.data?.trades ?? 0}</span>
        </div>
        <div className="pnl-chip">
          <span className="k">Open</span>
          <span className="v">{openPositions}</span>
        </div>

        <button className="btn-panic" onClick={() => setPanicOpen(true)}>
          Halt everything
        </button>
      </header>

      <main className="main">
        <div className="page">{children}</div>
      </main>

      {panicOpen && <PanicDialog onClose={() => setPanicOpen(false)} openPositions={openPositions} />}
      <style>{`.flip { transform: rotate(180deg); }`}</style>
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  tag,
  end,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  tag?: number;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      title={label}
    >
      <span className="icon">{icon}</span>
      <span className="label">{label}</span>
      {tag !== undefined && <span className="tag">{tag}</span>}
    </NavLink>
  );
}

/** Open or closed, and the exchange's own clock rather than the browser's. */
function MarketState({ open, nowIst }: { open?: boolean; nowIst?: string }) {
  const [clock, setClock] = useState("");

  useEffect(() => {
    // The server sends IST; the browser may be anywhere. Rendering the
    // exchange's clock rather than the machine's is the difference between
    // "the market shuts in ten minutes" being true and being a guess.
    const tick = () =>
      setClock(
        new Date().toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }),
      );
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <>
      <span className={`market-state ${open ? "open" : "closed"}`}>
        <span className={`pulse ${open ? "live" : ""}`} />
        {open ? "Market open" : "Market closed"}
      </span>
      <span className="clock" title={nowIst ? `Server: ${nowIst}` : undefined}>
        {clock} IST
      </span>
    </>
  );
}

/**
 * Whether prices are actually arriving.
 *
 * This is not decoration. A screen full of numbers that stopped updating four
 * minutes ago looks exactly like a screen that is working, and this chip is
 * the only thing that separates them.
 */
function FeedChip() {
  const live = useLive();

  if (!live.connected) {
    return <span className="feed-chip down">Reconnecting…</span>;
  }
  if (live.feed === "no-session") {
    return <span className="feed-chip">No Zerodha session</span>;
  }
  if (live.stale) {
    return (
      <span className="feed-chip stale" title="The last price arrived a while ago.">
        Prices {live.ageMs >= 0 ? ago(live.ageMs) : "not arriving"}
      </span>
    );
  }
  if (live.feed === "connected") {
    return (
      <span className="feed-chip connected">
        <span className="pulse live" /> Live
      </span>
    );
  }
  return <span className="feed-chip">{live.feed}</span>;
}

/**
 * The stop button.
 *
 * Two separate things, deliberately kept apart: halting stops anything NEW
 * from opening, and is what you want almost every time. Closing what is
 * already open is a second, explicit choice — it sells at the market, and on
 * a bad morning that is how a paper loss becomes a real one.
 */
function PanicDialog({ onClose, openPositions }: { onClose: () => void; openPositions: number }) {
  const [close, setClose] = useState(false);
  const panic = usePanic();
  const halt = useSetHalted();
  const toast = useToast();

  const run = () => {
    panic.mutate(close, {
      onSuccess: () => {
        toast.ok(
          close
            ? "Halted, and every open position was closed."
            : "Halted. Nothing new will be opened.",
        );
        onClose();
      },
      onError: (e) => toast.fail(e),
    });
  };

  return (
    <Modal
      title="Halt everything"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn danger" onClick={run} disabled={panic.isPending}>
            {panic.isPending ? <span className="spinner" /> : <IconBolt size={15} />}
            {close ? "Halt and close everything" : "Halt"}
          </button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        Halting stops every strategy from opening anything new, immediately. It does
        not touch what is already open, and it does not cancel an order that is
        already with Zerodha.
      </p>

      <div className="switch-row">
        <div>
          <div className="t">Also close the {openPositions} open position{openPositions === 1 ? "" : "s"}</div>
          <div className="d">
            Sells at the market, now. On a fast-moving morning that turns an
            unrealised loss into a realised one — which is sometimes exactly what
            you want, and sometimes not.
          </div>
        </div>
        <button
          className={`switch ${close ? "on" : ""}`}
          onClick={() => setClose((c) => !c)}
          aria-pressed={close}
          aria-label="Also close open positions"
          disabled={openPositions === 0}
        />
      </div>

      <p className="small faint mt">
        Nothing resumes on its own afterwards. You clear the halt in Settings when
        you have decided what happened.
      </p>

      {halt.isError && <div className="banner danger mt">{String(halt.error)}</div>}
    </Modal>
  );
}
